/* eslint-disable-next-line @typescript-eslint/consistent-type-imports */
import { AssemblyResult } from '@uppy/transloadit';
import type { AuthoringAssetCreationAttempts_AssetType as AuthoringAssetCreationAttemptsAssetType } from '__generated__/graphql-types';
import { find, values } from 'lodash';

import { AssetTypeToConstantMap } from 'bundles/asset-admin/constants/AssetTypes';
import type { AuthoringAssetCreationAttemptsMutationMutationVariables } from 'bundles/asset-admin/data/__generated__/AuthoringAssetCreationAttemptsMutation';
import type { AssetContext, PendingAssetCreationData } from 'bundles/asset-admin/types/assets';
import type { InternalMetadata, UppyFile } from 'bundles/asset-admin/types/uppy';

import ASSET_TYPES, {
  ALL_TYPES,
  AssetPdfTemplateToConstantMap,
  AssetVideoTemplateToConstantMap,
} from '../constants/AssetTypes';

type UppyFileMeta = InternalMetadata & { language: string | undefined };

export const getAssetType = (file: UppyFile | AssemblyResult) => {
  // Some file types (e.g. xml, yaml) end up being null after being processed by Transloadit
  if (!file.type) {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return ASSET_TYPES.Generic.name;
  }

  const lastDotIndex = file.name?.lastIndexOf('.') ?? -1;
  const extension = lastDotIndex > -1 ? (file.name?.substring(lastDotIndex + 1) ?? '') : '';

  const findType = (fileType: string, types: Array<string>) => {
    return find(types, (type) => !!fileType.match(type));
  };

  // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  if (findType(file.type, ASSET_TYPES.Image.types)) {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return ASSET_TYPES.Image.name;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  } else if (findType(file.type, ASSET_TYPES.Video.types) || ASSET_TYPES.Video.extensions?.includes(extension)) {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return ASSET_TYPES.Video.name;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  } else if (findType(file.type, ASSET_TYPES.Audio.types)) {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return ASSET_TYPES.Audio.name;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  } else if (findType(file.type, ASSET_TYPES.Pdf.types)) {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return ASSET_TYPES.Pdf.name;
    // only treat subtitle files as subtitle assets if they have a language code(https://coursera.atlassian.net/browse/CP-1664)
  } else if (
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    (findType(file.type, ASSET_TYPES.Subtitle.types) || ASSET_TYPES.Subtitle.extensions?.includes(extension)) &&
    (file.meta as UppyFileMeta).language
  ) {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return ASSET_TYPES.Subtitle.name;
  } else {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    return ASSET_TYPES.Generic.name;
  }
};

export const mapAssetTypeToMimes = (assetType: string) => {
  const category = values(ASSET_TYPES).find(({ name }) => name === assetType);

  // entry is an array of [name, types]
  return category ? category.types : ALL_TYPES;
};

export const getTypeSpecificOptions = (assetType: string) => {
  let options;
  switch (assetType) {
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    case ASSET_TYPES.Video.name: {
      options = {
        videoTemplate: 'fullVideo',
        tags: [
          {
            name: 'scope',
            value: 'video',
          },
        ],
      };
      break;
    }

    default: {
      options = { tags: [] };
      break;
    }
  }

  return options;
};

export const getAssetTypeData = ({
  pendingAssetCreationData,
  file,
}: {
  pendingAssetCreationData: PendingAssetCreationData & { assetType: AuthoringAssetCreationAttemptsAssetType };
  file: UppyFile;
}): AuthoringAssetCreationAttemptsMutationMutationVariables['input']['assetTypeWithRelatedData'] => {
  switch (pendingAssetCreationData.assetType) {
    case 'ASSET_TYPE_VIDEO':
      return {
        assetTypeVideoWithRelatedData: {
          assetType: pendingAssetCreationData.assetType,
          videoId: file.id,

          videoTemplate:
            AssetVideoTemplateToConstantMap[pendingAssetCreationData.videoTemplate || 'fullVideo'] ??
            'VIDEO_TEMPLATE_FULL_VIDEO',
        },
      };

    case 'ASSET_TYPE_PDF':
      return {
        assetTypePdfWithRelatedData: {
          assetType: pendingAssetCreationData.assetType,
          pdfTemplate:
            AssetPdfTemplateToConstantMap[pendingAssetCreationData.pdfTemplate || 'slides'] ?? 'PDF_TEMPLATE_SLIDES',
        },
      };

    case 'ASSET_TYPE_SUBTITLE':
      return {
        assetTypeSubtitleWithRelatedData: {
          assetType: pendingAssetCreationData.assetType,
          language: pendingAssetCreationData.language || '',
        },
      };

    case 'ASSET_TYPE_IMAGE':
      return {
        assetTypeImageWithRelatedData: {
          assetType: pendingAssetCreationData.assetType,
        },
      };

    case 'ASSET_TYPE_AUDIO':
      return {
        assetTypeAudioWithRelatedData: {
          assetType: pendingAssetCreationData.assetType,
        },
      };

    case 'ASSET_TYPE_GENERIC':
    default:
      return {
        assetTypeGenericWithRelatedData: {
          assetType: 'ASSET_TYPE_GENERIC',
        },
      };
  }
};

export const getAssetCreationInput = ({
  pendingAssetCreationData,
  file,
  context,
}: {
  pendingAssetCreationData: PendingAssetCreationData;
  file: UppyFile;
  context: AssetContext;
}): AuthoringAssetCreationAttemptsMutationMutationVariables['input'] => {
  const assetType = AssetTypeToConstantMap[pendingAssetCreationData?.assetType] || 'ASSET_TYPE_GENERIC';
  const assetCreationContext = {
    assetCreationCourseContext: {
      courseId: context.courseId,
    },
  };

  const baseInput = {
    assetCreationContext,
    assetTags: pendingAssetCreationData.tags,
    name: pendingAssetCreationData.name,
    ...(pendingAssetCreationData.creatorId && { creatorId: pendingAssetCreationData.creatorId }),
  };

  const assetTypeWithRelatedData = getAssetTypeData({
    pendingAssetCreationData: {
      ...pendingAssetCreationData,
      assetType,
    },
    file,
  });

  return {
    ...baseInput,
    assetTypeWithRelatedData,
  };
};
